import React, { useEffect, useRef } from "react";
import { HashLink } from "react-router-hash-link";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import pathMp4 from "./assets/connectvideo.mp4";
import connectMp4 from "./assets/connectvideo.mp4";
import Insightimage from "./assets/dashboard.jpg";
import Navbar from "./components/Navbar";


import {
  ArrowRight,
  SatelliteDish,
  Workflow,
  BarChart3,
  Megaphone,
  Layers,
  PhoneCall,
  Mic2,
  Image as ImageIcon,
  QrCode,
  Sparkles,
} from "lucide-react";
import kLogo from "./assets/k_logo_final.png";

// ===================== THEME =====================
const BG = "#0B0A17"; // dark/navy (keep)
const ACCENT = "#8B5CF6"; // violet (keep)

// Easing + base variants for scroll reveals
const fadeUp = (d = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6, delay: d } },
});

export default function SolutionsPage() {
  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: BG }}>
      <Navbar />
      {/* global gradient auras */}
      <BackgroundAuras />

      <main className="px-6 md:px-12 lg:px-20 pt-24">
        <Hero />
        <PathFlagship />
       <ConnectBlock />
<InsightBlock />
        <VoxField />
        <ReachStrip />
        <Suite360 />
        <ClosingCTA />
        <Footer />
      </main>
    </div>
  );
}

// ===================== HEADER =====================
function Header() {
  return (
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-black/20 backdrop-blur-md">
        <div className="w-full px-6 md:px-12 lg:px-20 py-4 flex items-center justify-between">
          {/* Logo + Brand */}
          <a href="/" className="flex items-center gap-3 group">
            <img
              src={kLogo}
              alt="Kurukshetra Analytics"
              className="h-8 w-auto transition duration-300 group-hover:scale-110"
            />
            <span className="font-semibold tracking-wide text-white">
              Kurukshetra Analytics
            </span>
          </a>
  
          {/* Nav Links */}
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/80">
          
            <HashLink smooth to="/#showcase" className="hover:text-white transition"> What we do</HashLink>
            <HashLink smooth to="/#insights" className="hover:text-white transition"> Insights</HashLink>
            <a className="hover:text-white transition" href="/solutions">Our Solutions</a>
            <a className="hover:text-white transition" href="/team">Our Team</a> 
            <a className="hover:text-white transition" href="/testimonials">Testimonials</a>
            <HashLink smooth to="/#getintouch" className="hover:text-white transition"> Contact Us</HashLink>
          </nav>
  
          {/* Contact Button */}
          <a
    href="https://wa.me/919876543210" // replace with your real number (no '+' sign, no spaces)
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-white hover:opacity-90"
    style={{fontSize: "14px"}}
    
  >
    📞 Call / WhatsApp: <span className="whitespace-nowrap">+91 99511 86186</span>
  </a>
  
        </div>
      </header>
    );
}

function BackgroundAuras() {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      {/* soft radial glows */}
      <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full blur-3xl opacity-20"
           style={{ background: `radial-gradient(40% 40% at 50% 50%, ${ACCENT}, transparent)` }} />
      <div className="absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full blur-3xl opacity-20"
           style={{ background: `radial-gradient(40% 40% at 50% 50%, #ffffff33, transparent)` }} />
    </div>
  );
}

// ===================== 1) HERO (Authority + intrigue) =====================
function Hero() {
  // gradient sweep on scroll
  const { scrollYProgress } = useScroll();
  const x = useTransform(scrollYProgress, [0, 0.2], ["0%", "-20%"]);

  return (
    <section id="top" className="relative min-h-[70vh] grid items-center py-16 md:py-24 overflow-hidden">
      {/* cinematic gradient sweep */}
      <motion.div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background: `linear-gradient(120deg, rgba(139,92,246,0.18), transparent 40%), radial-gradient(80% 60% at 80% 10%, rgba(139,92,246,0.25), transparent)`,
          x,
        }}
      />

      <div className="max-w-6xl grid lg:grid-cols-2 gap-10 items-center">
        <motion.div {...fadeUp(0)}>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Technology that <span className="bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(90deg, ${ACCENT}, #B794F4)` }}>Thinks Politically</span>
          </h1>
          <p className="mt-4 text-white/80 text-lg max-w-xl">
            Our solutions turn Data into Direction, Fieldwork into Foresight, and Presence into Performance.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <HashLink
              smooth
              to="#path"
              className="inline-flex items-center gap-2 rounded-2xl px-5 py-3 font-medium shadow-[0_0_0_1px_rgba(255,255,255,0.2)_inset]"
              style={{ backgroundColor: ACCENT }}
            >
              Explore the Suite <ArrowRight className="h-4 w-4" />
            </HashLink>
            <HashLink
              smooth
              to="/#getintouch"
              className="inline-flex items-center gap-2 rounded-2xl px-5 py-3 border border-white/25 hover:border-white/60 transition"
            >
              Talk to Us
            </HashLink>
            
          </div>
        </motion.div>

        {/* animated map grid / pulse */}
       
      </div>
    </section>
  );
}

function GridPulse() {
  return (
    <svg className="h-full w-full" viewBox="0 0 600 400" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="g" x1="0" x2="1">
          <stop offset="0%" stopColor="#ffffff22" />
          <stop offset="100%" stopColor="#ffffff05" />
        </linearGradient>
      </defs>
      {/* grid */}
      {[...Array(20)].map((_, i) => (
        <line key={`v${i}`} x1={i * 30} y1="0" x2={i * 30} y2="400" stroke="url(#g)" />
      ))}
      {[...Array(14)].map((_, i) => (
        <line key={`h${i}`} x1="0" y1={i * 28} x2="600" y2={i * 28} stroke="url(#g)" />
      ))}
      {/* pulsing nodes */}
      {[{ x: 120, y: 140 }, { x: 340, y: 220 }, { x: 480, y: 80 }].map((n, i) => (
        <motion.circle key={i} cx={n.x} cy={n.y} r="5" fill={ACCENT}
          animate={{ r: [5, 9, 5], opacity: [0.7, 1, 0.7] }} transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.4 }} />
      ))}
    </svg>
  );
}

// ===================== 2) PATH FLAGSHIP (Apple moment) =====================
function PathFlagship() {
  return (
    <section id="path" className="py-24">
      <motion.h2 {...fadeUp(0)} className="text-2xl md:text-3xl font-semibold">
        Kurukshetra Path — The Campaign Companion
      </motion.h2>
      <motion.p {...fadeUp(0.05)} className="mt-1 text-white/80 max-w-3xl">
        Reimagining how leaders walk, connect, and win.
      </motion.p>

    </section>
  );
}


function PathVideo() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Pause the video when scrolled out of view; play when visible
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!el) return;
        if (entry.isIntersecting) {
          // Try play; ignore promise errors (autoplay may be blocked)
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.35 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="relative h-[420px]">
      
        <div className="h-full w-full rounded-2xl bg-black/40 backdrop-blur-xl overflow-hidden">
          {/* Gradient edge highlight */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.06), transparent 40%)",
            }}
          />

          {/* The video */}
          <video
            ref={ref}
            className="h-full w-full object-cover"
            playsInline
            muted
            loop
            autoPlay
            preload="metadata"
           
            // controls  // uncomment if you want visible controls
          >
            {/* Prefer WebM when available */}
           
            <source src={pathMp4} type="video/mp4" />
            {/* Fallback text for older browsers */}
            Your browser does not support the video tag.
          </video>
        </div>
     

      {/* outer glow ring */}
      <div
        className="absolute -inset-3 rounded-[28px] opacity-25 blur-2xl"
        style={{
          background: `radial-gradient(40% 40% at 70% 30%, ${ACCENT}, transparent)`,
        }}
      />
    </div>
  );
}


function PathPhoneMock() {
  // simple points that suggest a walking path across the map
  const pathPoints = [
    { x: 40, y: 320 },
    { x: 120, y: 260 },
    { x: 200, y: 280 },
    { x: 260, y: 220 },
    { x: 340, y: 180 },
    { x: 420, y: 220 },
    { x: 520, y: 200 },
  ];

  // convert to SVG path "M ... L ..."
  const pathD =
    "M " +
    pathPoints
      .map((p, i) => (i === 0 ? `${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
      .join(" ");

  return (
    <div className="relative">
      {/* phone frame */}
      <div className="mx-auto w-[340px] h-[640px] rounded-[42px] border border-white/20 bg-black/40 backdrop-blur-xl shadow-2xl overflow-hidden">
        {/* notch */}
        <div className="mx-auto mt-2 h-5 w-28 rounded-b-2xl bg-black/70" />

        {/* screen area */}
        <div className="">
          {/* map-like tiled background */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(0deg, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />
          {/* subtle gradient */}
          <div
            className="absolute inset-0 opacity-50"
            style={{
              background: `radial-gradient(80% 60% at 80% 0%, ${ACCENT}22, transparent)`,
            }}
          />

          {/* SVG map layer */}
          <svg
            viewBox="0 0 600 400"
            className="relative block w-full h-[520px]"
            aria-label="Map preview: route and hotspots"
          >
            {/* animated route draw */}
            <motion.path
              d={pathD}
              fill="none"
              stroke={ACCENT}
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              transition={{ duration: 1.8, ease: "easeInOut" }}
              style={{ filter: "drop-shadow(0 0 6px rgba(139,92,246,0.6))" }}
            />

            {/* leader dot that moves along path */}
            <motion.circle
              r="6"
              fill="#fff"
              stroke={ACCENT}
              strokeWidth="2"
              animate={{
                cx: pathPoints.map((p) => p.x),
                cy: pathPoints.map((p) => p.y),
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* hotspot pins with labels */}
            {[
              { x: 120, y: 260, label: "Booth A12" },
              { x: 340, y: 180, label: "Meeting Point" },
              { x: 520, y: 200, label: "Follow-up" },
            ].map((m, i) => (
              <g key={i}>
                <circle cx={m.x} cy={m.y} r="8" fill={ACCENT} />
                <circle cx={m.x} cy={m.y} r="3" fill="#fff" />
                <rect
                  x={m.x + 10}
                  y={m.y - 14}
                  rx="6"
                  ry="6"
                  width="110"
                  height="22"
                  fill="rgba(0,0,0,0.6)"
                  stroke="rgba(255,255,255,0.25)"
                />
                <text
                  x={m.x + 16}
                  y={m.y + 2}
                  fontSize="10"
                  fill="#ffffff"
                  style={{ fontFamily: "ui-sans-serif, system-ui" }}
                >
                  {m.label}
                </text>
              </g>
            ))}
          </svg>

          {/* legend chips (explain symbols) */}
          <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-2">
            {[
              { swatch: "bg-white", text: "Leader", outline: true },
              { swatch: "", text: "Booth A12", accent: true },
              { swatch: "", text: "Follow-up due", border: true },
            ].map((c) => (
              <div
                key={c.text}
                className={`px-2.5 py-1 rounded-xl text-[11px] ${
                  c.accent
                    ? "bg-white/10 border border-white/20"
                    : c.border
                    ? "border border-white/20"
                    : c.outline
                    ? "border border-white/20 bg-black/40"
                    : "bg-white/5"
                }`}
              >
                {c.text}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


// ===================== CONNECT (stacked section) =====================
function ConnectBlock() {
  return (
    <section id="connect" className="py-24">
        <motion.h2 {...fadeUp(0)} className="text-2xl md:text-3xl font-semibold">
        Kurukshetra Connect — The Command Center
      </motion.h2>
      <motion.p {...fadeUp(0.05)} className="mt-1 text-white/80 max-w-3xl">
        An intelligent platform for organized campaign control.
      </motion.p>
      
    </section>
  );
}

function ConnectVideo() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Pause the video when scrolled out of view; play when visible
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!el) return;
        if (entry.isIntersecting) {
          // Try play; ignore promise errors (autoplay may be blocked)
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.35 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="relative h-[420px]">
      
        <div className="h-full w-full rounded-2xl bg-black/40 backdrop-blur-xl overflow-hidden">
          {/* Gradient edge highlight */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.06), transparent 40%)",
            }}
          />

          {/* The video */}
          <video
            ref={ref}
            className="h-full w-full object-cover"
            playsInline
            muted
            loop
            autoPlay
            preload="metadata"
           
            // controls  // uncomment if you want visible controls
          >
            {/* Prefer WebM when available */}
           
            <source src={connectMp4} type="video/mp4" />
            {/* Fallback text for older browsers */}
            Your browser does not support the video tag.
          </video>
        </div>
     

      {/* outer glow ring */}
      <div
        className="absolute -inset-3 rounded-[28px] opacity-25 blur-2xl"
        style={{
          background: `radial-gradient(40% 40% at 70% 30%, ${ACCENT}, transparent)`,
        }}
      />
    </div>
  );
}

// ===================== INSIGHT (stacked section) =====================
function InsightBlock() {
  return (
    <section id="insight" className="py-24">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
        {/* Image (dashboard). Replace src later */}
       
        {/* Copy */}
        <motion.div {...fadeUp(0)}>
          <div className="flex items-center gap-3 mb-3">
            
            <h3 className="text-2xl md:text-3xl font-semibold">Kurukshetra Insight</h3>
          </div>

          <p className="text-white/80 leading-relaxed max-w-2xl">
            Turn data into direction. 
          </p>

        </motion.div>
      </div>
    </section>
  );
}


// ===================== 4) VOXFIELD (Human + authentic) =====================
function VoxField() {
  return (
    <section className="py-24">
      <motion.h3 {...fadeUp(0)} className="text-2xl md:text-3xl font-semibold">JanaVaani — Listening, Continuously.</motion.h3>
      <motion.p {...fadeUp(0.05)} className="mt-1 text-white/80 max-w-3xl">Real‑time ground feedback with voice notes, photos, and quick polls flowing to leadership views.</motion.p>

      
    </section>
  );
}



// ===================== 5) REACH (Strip band) =====================
function ReachStrip() {
  return (
    <section className="py-12">
      <div className=" px-6 py-10 overflow-hidden relative">
        <motion.h3 {...fadeUp(0)} className="text-xl md:text-2xl font-semibold">Kurukshetra Reach — Connect. Scan. Engage.</motion.h3>
        <p className="text-white/80">Turning local interactions into measurable digital visibility.</p>
        
        <motion.div
          aria-hidden
          className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center gap-4"
          initial={{ opacity: 0.6 }} animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 3, repeat: Infinity }}
        > </motion.div>
      </div>
    </section>
  );
}

// ===================== 6) 360 (Ecosystem orbit) =====================
function Suite360() {
  const tools = ["Path", "Connect", "Insight", "VoxField", "Reach"];
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 -z-10" style={{ background: `radial-gradient(60% 60% at 50% 40%, ${ACCENT}22, transparent)` }} />
      <div className="text-center">
        <motion.h3 {...fadeUp(0)} className="text-2xl md:text-3xl font-semibold">Kurukshetra 360™ — One Ecosystem, Total Clarity.</motion.h3>
        <motion.p {...fadeUp(0.05)} className="mt-1 text-white/80">All your campaign intelligence, seamlessly connected.</motion.p>
      </div>

      

     
    </section>
  );
}

// ===================== 7) CLOSING CTA =====================
function ClosingCTA() {
  return (
    <section id="getintouch" className="py-24">
      <div className="relative rounded-3xl overflow-hidden border border-white/10">
        <div className="absolute inset-0 -z-10" style={{ background: `linear-gradient(0deg, rgba(0,0,0,0.6), rgba(0,0,0,0.6)), radial-gradient(60% 60% at 50% 50%, ${ACCENT}22, transparent)` }} />
        <div className="px-6 md:px-12 lg:px-16 py-16 text-center">
          <h3 className="text-3xl font-semibold">Let’s Plan Your Win Path.</h3>
          <p className="mt-3 text-white/75 max-w-2xl mx-auto">Every campaign deserves clarity, coordination, and conviction. Let’s build yours.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a
              href="/#getintouch"
              className="inline-flex items-center gap-2 rounded-2xl px-5 py-3 font-medium"
              style={{ backgroundColor: ACCENT }}
            >
              Schedule a Consultation <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="https://wa.me/919951186186"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-2xl px-5 py-3 border border-white/25 hover:border-white/60 transition"
            >
              Or WhatsApp us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-8 border-t border-white/10 text-sm text-white/60">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <img src={kLogo} alt="Kurukshetra Analytics" className="h-6 w-auto" />
          <span>© {new Date().getFullYear()} Kurukshetra Analytics</span>
        </div>
        
      </div>
    </footer>
  );
}
